// useRulesValidation.js
import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';

const withMsg = (ruleId, msg) => ({ ruleId, msg });

/**
 * Centralised validation hook for scheduling rules.
 */
export default function useRulesValidation(rules = [], allEmployees = []) {
  const [errors, setErrors] = useState([]);

  /**
   * Pure function to compute validation errors.
   */
  const computeErrors = useCallback(() => {
    const next = [];
    const employeesHashes = {};
    let hasApplyAll = false;

    // Rule-level validations
    rules.forEach((r, idx) => {
      // Track apply-all
      if (r.applyToAll) hasApplyAll = true;

      // 1) Assignment required
      if (!r.applyToAll && (!r.employees || r.employees.length === 0)) {
        next.push(withMsg(r.id, 'Assign at least one employee'));
      }

      // 2) Duplicate employee assignment across rules
      r.employees?.forEach((id) => {
        if (employeesHashes[id]) {
          next.push(withMsg(r.id, `Duplicate assignment for employee ${id}`));
        }
        employeesHashes[id] = true;
      });

      // 3) Numeric fields must be > 0 (except minDaysOffPerWeek and shift start times)
      const { employeeLimits, shiftRules } = r.config;
      const {
        minHoursPerWeek = 0,
        maxHoursPerWeek = 0,
        maxDaysOffPerYear = 0,
        maxSickDaysPerYear = 0
      } = employeeLimits;

      if (minHoursPerWeek <= 0) {
        next.push(withMsg(r.id, 'Min hours per week must be greater than 0'));
      }
      if (maxHoursPerWeek <= 0) {
        next.push(withMsg(r.id, 'Max hours per week must be greater than 0'));
      }
      if (maxDaysOffPerYear <= 0) {
        next.push(withMsg(r.id, 'Max paid days off per year must be greater than 0'));
      }
      if (maxSickDaysPerYear <= 0) {
        next.push(withMsg(r.id, 'Max paid sick days per year must be greater than 0'));
      }

      // 4) Max > Min hours/week
      if (maxHoursPerWeek <= minHoursPerWeek) {
        next.push(withMsg(r.id, 'Max hours/week must exceed Min hours/week'));
      }

      // 5) Duplicate rules (same employee set)
      const key = r.applyToAll ? '*ALL*' : JSON.stringify((r.employees || []).sort());
      r._ruleKey = key; // tag for later
    });

// ➋ Detect duplicate *rule definitions* (same limits / shift options)
//     – irrespectively of which employees they apply to.
    const configSeen = {};

    rules.forEach((r) => {
      /* A stable, order-independent signature of the rule’s config */
      const cfgKey = JSON.stringify({
        employeeLimits: r.config?.employeeLimits ?? {},
        shiftRules: {
          ...r.config?.shiftRules,
          // sort so "09:00, 17:00" === "17:00, 09:00"
          preferredShiftStartTimes: [
            ...(r.config?.shiftRules?.preferredShiftStartTimes ?? []),
          ].sort(),
        },
      });

      if (configSeen[cfgKey]) {
        next.push(
          withMsg(
            r.id,
            "Duplicate rule – merge the employees into one rule instead of repeating identical limits"
          )
        );
      }
      configSeen[cfgKey] = true;
    });

    // 6) Coverage: each employee must appear in at least one rule
    const allIds = allEmployees.map((e) => e.id);
    const covered = new Set();
    rules.forEach((r) => {
      if (r.applyToAll) {
        allIds.forEach((id) => covered.add(id));
      } else {
        r.employees?.forEach((id) => covered.add(id));
      }
    });
    allIds.forEach((id) => {
      if (!covered.has(id)) {
        next.push(withMsg('*', `Every service-provider must have a rule.`));
      }
    });

    // 7) Cannot mix apply-all with others
    if (hasApplyAll && rules.length > 1) {
      rules.forEach((r) => {
        next.push(
          withMsg(
            r.id,
            r.applyToAll
              ? '“Apply to all” must be the only rule.'
              : 'Cannot mix “Apply to all” with other rules.'
          )
        );
      });
    }

    // 8) Rule count cannot exceed number of employees
    if (rules.length > allEmployees.length) {
      next.push(withMsg('*', 'Number of rules cannot exceed number of employees'));
    }

    return next;
  }, [rules, allEmployees]);

  // Sync errors state
  useEffect(() => {
    setErrors(computeErrors());
  }, [rules, allEmployees]);

  // Triggered on Save
  const validateAll = () => {
    const next = computeErrors();
    setErrors(next);
    if (next.length) toast.error('Please resolve validation errors before saving.');
    return next.length === 0;
  };

  return { errors, validateAll };
}
